import { useEffect, useState } from 'react'
import { INVITE_CODE_LENGTH, formatCode, isValidCode, normalizeCode } from '../../shared/code'
import type { Role } from '../../shared/types'
import * as api from '../api'
import { Button, Spinner } from '../components/ui'
import { LOCALES, localeName, useI18n, type Dict } from '../i18n'
import { useStore } from '../store'

/**
 * 未登录时的入口。三种模式共用同一个外壳：
 *   login  —— 已有账号
 *   signup —— 开一个新家庭（免费，1 个席位）
 *   join   —— 拿着邀请码加入别人的家庭
 *
 * 邀请链接形如 /?invite=ABCD-EFGH，带码进来直接落在 join 上，
 * 对方不用先弄明白「注册」和「加入」有什么区别。
 */
type Mode = 'login' | 'signup' | 'join' | 'forgot' | 'reset'

const inputClass =
  'w-full rounded-2xl bg-card border border-line px-4 py-3.5 text-[16px] outline-none focus:border-accent transition'

/**
 * 从 URL 里取一个一次性参数，取到就顺手把 query 抹掉。
 *
 * 抹掉不只是为了好看：邀请码和重置 token 都是凭证，留在地址栏里会跟着
 * 浏览器历史、书签、以及「分享这个页面」一起跑出去。
 */
function paramFromUrl(key: string, valid: (v: string) => boolean): string {
  try {
    const v = new URL(window.location.href).searchParams.get(key)
    if (!v || !valid(v)) return ''
    window.history.replaceState(null, '', window.location.pathname)
    return v
  } catch {
    return ''
  }
}

function headings(t: Dict): Record<Mode, { title: string; subtitle: string }> {
  return {
    login: { title: t.login.title, subtitle: t.login.subtitle },
    signup: { title: t.signup.title, subtitle: t.signup.subtitle },
    join: { title: t.join.title, subtitle: t.join.subtitle },
    forgot: { title: t.forgot.title, subtitle: t.forgot.subtitle },
    reset: { title: t.reset.title, subtitle: t.reset.logoutNote },
  }
}

export function Auth() {
  const { t } = useI18n()
  const [initialCode] = useState(() =>
    normalizeCode(paramFromUrl('invite', (v) => isValidCode(v, INVITE_CODE_LENGTH))),
  )
  const [resetToken] = useState(() => paramFromUrl('reset', (v) => v.length >= 20))
  const [mode, setMode] = useState<Mode>(
    resetToken ? 'reset' : initialCode ? 'join' : 'login',
  )

  // 发信没配好的时候不显示「忘记口令了？」—— 点进去填完邮箱、然后永远收不到信，
  // 比压根没有这个入口更糟：用户会反复试，会以为是自己邮箱填错了。
  // 默认 false，探测成功才显示，这样万一请求失败也是「少一个链接」而不是「多一个死路」。
  const [emailRecovery, setEmailRecovery] = useState(false)
  useEffect(() => {
    let cancelled = false
    api
      .fetchConfig()
      .then((cfg) => {
        if (!cancelled) setEmailRecovery(cfg.emailRecovery)
      })
      .catch(() => {
        /* 探测失败就当没有，不影响登录本身 */
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="min-h-full flex flex-col justify-center px-6 py-12">
      <div className="w-full max-w-sm mx-auto">
        <div className="text-center mb-8">
          <img
            src="/icons/icon.svg"
            alt=""
            width="64"
            height="64"
            className="w-16 h-16 rounded-2xl mx-auto mb-5"
          />
          <h1 className="text-2xl font-semibold">{headings(t)[mode].title}</h1>
          <p className="text-sm text-muted mt-2 leading-relaxed">{headings(t)[mode].subtitle}</p>
        </div>

        {mode === 'login' && <LoginForm />}
        {mode === 'signup' && <SignupForm />}
        {mode === 'join' && <JoinForm initialCode={initialCode} />}
        {mode === 'forgot' && <ForgotForm />}
        {mode === 'reset' && <ResetForm token={resetToken} />}

        {/* 重置页不给切换入口：手里已经有链接了，这时候引导去别的地方只会添乱 */}
        {mode !== 'reset' && (
          <div className="mt-7 space-y-2 text-center">
            {mode === 'login' && emailRecovery && (
              <SwitchLink onClick={() => setMode('forgot')}>{t.login.toForgot}</SwitchLink>
            )}
            {mode !== 'signup' && (
              <SwitchLink onClick={() => setMode('signup')}>{t.login.toSignup}</SwitchLink>
            )}
            {mode !== 'join' && (
              <SwitchLink onClick={() => setMode('join')}>{t.login.toJoin}</SwitchLink>
            )}
            {mode !== 'login' && (
              <SwitchLink onClick={() => setMode('login')}>{t.login.toLogin}</SwitchLink>
            )}
          </div>
        )}

        {/* 登录之前也得能换语言：这时候设置页还进不去，
            英文用户落在一屏中文上就只能猜哪个按钮是登录 */}
        <LanguageSwitch />
      </div>
    </div>
  )
}

/** 未登录页底部那一行语言。每个名字都用它自己那门语言写。 */
function LanguageSwitch() {
  const { locale, setLocale } = useI18n()
  if (LOCALES.length < 2) return null
  return (
    <div className="mt-8 flex items-center justify-center gap-2">
      {LOCALES.map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => setLocale(l)}
          aria-pressed={l === locale}
          className={`px-3 py-1.5 rounded-full text-xs transition ${
            l === locale ? 'bg-card border border-line text-ink' : 'text-muted'
          }`}
        >
          {localeName(l)}
        </button>
      ))}
    </div>
  )
}

function SwitchLink({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full text-sm text-muted hover:text-accent transition py-1"
    >
      {children}
    </button>
  )
}

function ErrorText({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-period px-1 leading-relaxed">{children}</p>
}

function SubmitButton({ busy, label, working }: { busy: boolean; label: string; working: string }) {
  return (
    <Button type="submit" className="w-full mt-2" disabled={busy}>
      {busy ? (
        <span className="inline-flex items-center gap-2">
          <Spinner /> {working}
        </span>
      ) : (
        label
      )}
    </Button>
  )
}

// ── 登录 ────────────────────────────────────────────────────────────────

function LoginForm() {
  const { t } = useI18n()
  const { signIn } = useStore()
  const [name, setName] = useState('')
  const [passphrase, setPassphrase] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !passphrase) return
    setBusy(true)
    setError(null)
    try {
      // 这一步慢是正常的：口令要在本机做 60 万次哈希才发出去
      await signIn(name.trim(), passphrase)
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error)
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <input
        type="text"
        inputMode="text"
        autoCapitalize="none"
        autoCorrect="off"
        autoComplete="username"
        placeholder={t.login.name}
        value={name}
        onChange={(e) => setName(e.target.value)}
        className={inputClass}
      />
      <input
        type="password"
        autoComplete="current-password"
        placeholder={t.login.passphrase}
        value={passphrase}
        onChange={(e) => setPassphrase(e.target.value)}
        className={inputClass}
      />
      {error && <ErrorText>{error}</ErrorText>}
      <SubmitButton busy={busy} label={t.login.submit} working={t.login.working} />
      <p className="text-xs text-muted text-center pt-2 leading-relaxed">{t.login.hint}</p>
    </form>
  )
}

// ── 注册 ────────────────────────────────────────────────────────────────

function SignupForm() {
  const { t } = useI18n()
  const { signUp } = useStore()
  const [loginName, setLoginName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [householdName, setHouseholdName] = useState('')
  const [role, setRole] = useState<Role>('tracker')
  const [passphrase, setPassphrase] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (passphrase.length < 8) return setError(t.settings.passwordTooShort)
    if (passphrase !== confirm) return setError(t.settings.passwordMismatch)

    setBusy(true)
    setError(null)
    try {
      await signUp({
        loginName: loginName.trim().toLowerCase(),
        displayName: displayName.trim() || loginName.trim(),
        email: email.trim(),
        householdName: householdName.trim() || t.signup.defaultHouseholdName,
        role,
        passphrase,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error)
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <input
        type="text"
        inputMode="text"
        autoCapitalize="none"
        autoCorrect="off"
        autoComplete="username"
        placeholder={t.login.name}
        value={loginName}
        onChange={(e) => setLoginName(e.target.value)}
        className={inputClass}
      />
      <input
        type="text"
        autoComplete="nickname"
        placeholder={t.signup.displayName}
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        className={inputClass}
      />
      <input
        type="text"
        placeholder={t.signup.householdName}
        value={householdName}
        onChange={(e) => setHouseholdName(e.target.value)}
        className={inputClass}
      />

      <fieldset className="pt-2">
        <legend className="text-sm text-muted px-1 mb-2">{t.signup.roleQuestion}</legend>
        <div className="grid grid-cols-2 gap-2">
          <RoleOption
            active={role === 'tracker'}
            onClick={() => setRole('tracker')}
            label={t.signup.roleSelf}
          />
          <RoleOption
            active={role === 'partner'}
            onClick={() => setRole('partner')}
            label={t.signup.rolePartner}
          />
        </div>
        <p className="text-xs text-muted mt-2 px-1 leading-relaxed">{t.signup.roleHint}</p>
      </fieldset>

      <input
        type="password"
        autoComplete="new-password"
        placeholder={t.signup.passphrase}
        value={passphrase}
        onChange={(e) => setPassphrase(e.target.value)}
        className={inputClass}
      />
      <input
        type="password"
        autoComplete="new-password"
        placeholder={t.signup.confirmPassphrase}
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        className={inputClass}
      />
      <input
        type="email"
        inputMode="email"
        autoCapitalize="none"
        autoCorrect="off"
        autoComplete="email"
        placeholder={t.signup.email}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className={inputClass}
      />
      <p className="text-xs text-muted px-1 leading-relaxed">{t.signup.emailHint}</p>

      {error && <ErrorText>{error}</ErrorText>}
      <SubmitButton busy={busy} label={t.signup.submit} working={t.signup.working} />

      {/* 没有邮件找回的情况下，这句必须显眼 —— 忘口令是真的没救 */}
      <p className="text-xs text-period text-center pt-2 leading-relaxed">{t.signup.warning}</p>
    </form>
  )
}

function RoleOption({
  active,
  onClick,
  label,
}: {
  active: boolean
  onClick: () => void
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`min-h-[44px] rounded-2xl border text-sm transition active:scale-[0.97] ${
        active ? 'bg-accent text-on-accent border-accent' : 'bg-card border-line text-ink/80'
      }`}
    >
      {label}
    </button>
  )
}

// ── 忘记口令 ────────────────────────────────────────────────────────────

function ForgotForm() {
  const { t } = useI18n()
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.forgotPassword(email.trim())
      // 服务端对「邮箱存不存在」一律回同样的结果，界面也必须一样，
      // 否则前端就把服务端小心藏起来的信息又漏出去了
      setSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error)
    } finally {
      setBusy(false)
    }
  }

  if (sent) {
    return (
      <div className="rounded-2xl bg-fertile/10 border border-fertile/30 px-4 py-4">
        <p className="text-sm leading-relaxed">{t.forgot.done}</p>
      </div>
    )
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <input
        type="email"
        inputMode="email"
        autoCapitalize="none"
        autoCorrect="off"
        autoComplete="email"
        placeholder={t.forgot.email}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className={inputClass}
      />
      {error && <ErrorText>{error}</ErrorText>}
      <SubmitButton busy={busy} label={t.forgot.submit} working={t.forgot.working} />
      <p className="text-xs text-muted text-center pt-2 leading-relaxed">
        {t.forgot.noEmailHint}
      </p>
    </form>
  )
}

// ── 用邮件里的链接重设口令 ──────────────────────────────────────────────

function ResetForm({ token }: { token: string }) {
  const { t } = useI18n()
  const { resetPassword } = useStore()
  const [account, setAccount] = useState<{ loginName: string; displayName: string } | null>(null)
  const [checking, setChecking] = useState(true)
  const [passphrase, setPassphrase] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // 先拿 token 换登录名 —— 口令派生要用它当盐，不知道就算不出正确的 clientKey
  useEffect(() => {
    let cancelled = false
    api
      .peekReset(token)
      .then((a) => {
        if (!cancelled) setAccount(a)
      })
      .catch(() => {
        if (!cancelled) setError(t.reset.invalid)
      })
      .finally(() => {
        if (!cancelled) setChecking(false)
      })
    return () => {
      cancelled = true
    }
  }, [token])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!account) return
    if (passphrase.length < 8) return setError(t.settings.passwordTooShort)
    if (passphrase !== confirm) return setError(t.settings.passwordMismatch)

    setBusy(true)
    setError(null)
    try {
      await resetPassword(token, account.loginName, passphrase)
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error)
      setBusy(false)
    }
  }

  if (checking) return <p className="text-sm text-muted text-center">{t.reset.checking}</p>
  if (!account) return <ErrorText>{error ?? t.reset.invalid}</ErrorText>

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="rounded-2xl bg-fertile/10 border border-fertile/30 px-4 py-3">
        <p className="text-sm">{t.reset.forAccount(account.displayName)}</p>
        <p className="text-xs text-muted mt-1">{account.loginName}</p>
      </div>
      <input
        type="password"
        autoComplete="new-password"
        placeholder={t.reset.passphrase}
        value={passphrase}
        onChange={(e) => setPassphrase(e.target.value)}
        className={inputClass}
      />
      <input
        type="password"
        autoComplete="new-password"
        placeholder={t.reset.confirmPassphrase}
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        className={inputClass}
      />
      {error && <ErrorText>{error}</ErrorText>}
      <SubmitButton busy={busy} label={t.reset.submit} working={t.reset.working} />
      <p className="text-xs text-muted text-center pt-2 leading-relaxed">{t.signup.warning}</p>
    </form>
  )
}

// ── 用邀请码加入 ────────────────────────────────────────────────────────

function JoinForm({ initialCode }: { initialCode: string }) {
  const { t } = useI18n()
  const { joinWithCode } = useStore()
  const [code, setCode] = useState(initialCode ? formatCode(initialCode) : '')
  const [preview, setPreview] = useState<api.InvitePreview | null>(null)
  const [checking, setChecking] = useState(false)
  const [loginName, setLoginName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [passphrase, setPassphrase] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // 码填够 8 位就自动去核一下，把「谁邀请你」显示出来。
  // 让对方在填一长串表单之前先确认没走错门。
  const normalized = normalizeCode(code)
  useEffect(() => {
    if (!isValidCode(normalized, INVITE_CODE_LENGTH)) {
      setPreview(null)
      return
    }
    let cancelled = false
    setChecking(true)
    api
      .peekInvite(normalized)
      .then((p) => {
        if (!cancelled) setPreview(p)
      })
      .catch(() => {
        if (!cancelled) setPreview(null)
      })
      .finally(() => {
        if (!cancelled) setChecking(false)
      })
    return () => {
      cancelled = true
    }
  }, [normalized])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValidCode(normalized, INVITE_CODE_LENGTH))
      return setError(t.join.codeLength(INVITE_CODE_LENGTH))
    if (passphrase.length < 8) return setError(t.settings.passwordTooShort)
    if (passphrase !== confirm) return setError(t.settings.passwordMismatch)

    setBusy(true)
    setError(null)
    try {
      await joinWithCode(normalized, {
        loginName: loginName.trim().toLowerCase(),
        displayName: displayName.trim() || loginName.trim(),
        email: email.trim(),
        passphrase,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error)
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <input
        type="text"
        inputMode="text"
        autoCapitalize="characters"
        autoCorrect="off"
        placeholder={t.join.code}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        className={`${inputClass} tracking-[0.2em] uppercase`}
      />
      {checking && <p className="text-xs text-muted px-1">{t.join.checking}</p>}
      {preview && (
        <div className="rounded-2xl bg-fertile/10 border border-fertile/30 px-4 py-3">
          <p className="text-sm">{t.join.invitedBy(preview.inviterName, preview.householdName)}</p>
          <p className="text-xs text-muted mt-1 leading-relaxed">{t.join.sameDataHint}</p>
        </div>
      )}

      <input
        type="text"
        inputMode="text"
        autoCapitalize="none"
        autoCorrect="off"
        autoComplete="username"
        placeholder={t.login.name}
        value={loginName}
        onChange={(e) => setLoginName(e.target.value)}
        className={inputClass}
      />
      <input
        type="text"
        autoComplete="nickname"
        placeholder={t.signup.displayName}
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        className={inputClass}
      />
      <input
        type="password"
        autoComplete="new-password"
        placeholder={t.signup.passphrase}
        value={passphrase}
        onChange={(e) => setPassphrase(e.target.value)}
        className={inputClass}
      />
      <input
        type="password"
        autoComplete="new-password"
        placeholder={t.signup.confirmPassphrase}
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        className={inputClass}
      />
      <input
        type="email"
        inputMode="email"
        autoCapitalize="none"
        autoCorrect="off"
        autoComplete="email"
        placeholder={t.signup.email}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className={inputClass}
      />

      {error && <ErrorText>{error}</ErrorText>}
      <SubmitButton busy={busy} label={t.join.submit} working={t.join.working} />
      <p className="text-xs text-period text-center pt-2 leading-relaxed">{t.signup.warning}</p>
    </form>
  )
}
